import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Loader2, ArrowLeft, Check, Minus, CircleAlert } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";
import { WhatsAppFloat, MobileStickyCTA } from "@/components/landing/Conversion";
import { comparePackages, estimatePackage, getCompare, setCompare, getTravellers, inr } from "@/lib/api";

const cell = "px-4 py-3 text-sm text-stone-700 align-top";
const labelCell = "px-4 py-3 text-[11px] font-black uppercase tracking-wider text-stone-500 align-top whitespace-nowrap";

export default function ComparePage() {
    const [selection, setSelection] = useState(() => getCompare());
    const [packages, setPackages] = useState(null);
    const [tiers, setTiers] = useState({});
    const [totals, setTotals] = useState({});
    const trav = getTravellers();

    useEffect(() => {
        const h = () => setSelection(getCompare());
        window.addEventListener("gc-compare-changed", h);
        return () => window.removeEventListener("gc-compare-changed", h);
    }, []);

    useEffect(() => {
        document.title = "Compare Packages | GhumneChaley.com";
        if (selection.length === 0) { setPackages([]); return; }
        comparePackages(selection.map((s) => s.slug)).then(async (res) => {
            setPackages(res.packages);
            const t = {};
            res.packages.forEach((p) => { t[p.slug] = p.tiers?.[0]?.label || ""; });
            setTiers(t);
            // server-side indicative totals for the saved traveller context (self-arranged transport)
            const entries = await Promise.all(res.packages.map(async (p) => {
                if (p.enquiry_only) return [p.slug, null];
                try {
                    const est = await estimatePackage(p.slug, { tier: p.tiers?.[0]?.label, adults: trav.adults, children: trav.children, rooms: 1, transport_mode: "self" });
                    return [p.slug, est.total];
                } catch { return [p.slug, null]; }
            }));
            setTotals(Object.fromEntries(entries));
        }).catch(() => setPackages([]));
        // eslint-disable-next-line
    }, [selection]);

    const tierOf = (p) => (p.tiers || []).find((t) => t.label === tiers[p.slug]) || p.tiers?.[0] || {};

    const rows = useMemo(() => {
        if (!packages) return [];
        const g = (fn) => (p) => fn(p, tierOf(p));
        return [
            { key: "destination", label: "Destination", get: g((p) => p.destination_name) },
            { key: "duration", label: "Duration", get: g((p) => `${p.days} Days / ${p.nights} Nights`) },
            { key: "dates", label: "Travel dates", get: g((p) => (p.departures || []).length ? p.departures.map((d) => `${new Date(d.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} (${d.status})`).join(", ") : "Dates on request") },
            { key: "depart", label: "Departure cities", get: g((p) => (p.departure_cities || []).join(", ") || "—") },
            { key: "price", label: "Price per person", get: g((p, t) => t.price_adult != null ? inr(t.price_adult) : "On enquiry"), money: true },
            { key: "total", label: `Est. group total (${trav.adults + trav.children} travellers)`, get: g((p) => p.enquiry_only ? "Quoted per group" : (totals[p.slug] != null ? inr(totals[p.slug]) : "—")), money: true },
            { key: "hotel", label: "Hotel", get: g((p, t) => t.hotel || p.hotel_category || "—") },
            { key: "room", label: "Room sharing", get: g((p, t) => t.room_type || "—") },
            { key: "meals", label: "Meals", get: g((p, t) => t.meals || p.meal_plan || "—") },
            { key: "tickets", label: "Tickets / transport", get: g((p, t) => t.transport || p.transport_type || "—") },
            { key: "sightseeing", label: "Sightseeing", get: g((p, t) => t.sightseeing || "—") },
            { key: "activities", label: "Activities", get: g((p, t) => t.activities || "—") },
            { key: "coordinator", label: "Tour coordinator", get: g((p, t) => t.service_level || "—") },
            { key: "insurance", label: "Travel insurance", get: g((p) => (p.inclusions || []).some((i) => /insurance/i.test(i)) ? "Included" : (p.addons || []).some((a) => /insurance/i.test(a.name)) ? "Optional add-on" : "Not included") },
            { key: "taxes", label: "Taxes & fees", get: g((p) => `${p.taxes_pct || 0}% GST${p.fees_fixed ? ` + ${inr(p.fees_fixed)} fees` : ""}`) },
            { key: "inclusions", label: "Inclusions", get: g((p) => `${(p.inclusions || []).length} items — ${(p.inclusions || []).slice(0, 3).join(", ")}${(p.inclusions || []).length > 3 ? "…" : ""}`) },
            { key: "exclusions", label: "Exclusions", get: g((p) => `${(p.exclusions || []).length} items — ${(p.exclusions || []).slice(0, 3).join(", ")}${(p.exclusions || []).length > 3 ? "…" : ""}`) },
            { key: "cancellation", label: "Cancellation policy", get: g((p) => p.cancellation_policy || "—") },
            { key: "payment", label: "Payment terms", get: g((p) => p.payment_terms || "—") },
            { key: "availability", label: "Availability", get: g((p) => p.enquiry_only ? "Enquiry only" : (p.departures || []).some((d) => d.status === "available") ? "Departures open" : "On request") },
        ];
        // eslint-disable-next-line
    }, [packages, tiers, totals]);

    const diffKeys = useMemo(() => {
        if (!packages || packages.length < 2) return new Set();
        return new Set(rows.filter((r) => new Set(packages.map(r.get)).size > 1).map((r) => r.key));
    }, [packages, rows]);

    const daysList = (packages || []).map((p) => p.days);
    const durationDiffers = new Set(daysList).size > 1;

    const remove = (slug) => setCompare(selection.filter((s) => s.slug !== slug));

    const insuranceIcon = (v) => v === "Included"
        ? <span className="inline-flex items-center gap-1 text-green-700 font-bold"><Check className="h-3.5 w-3.5" />{v}</span>
        : v === "Optional add-on"
            ? <span className="inline-flex items-center gap-1 text-amber-600 font-bold"><CircleAlert className="h-3.5 w-3.5" />{v}</span>
            : <span className="inline-flex items-center gap-1 text-stone-400"><Minus className="h-3.5 w-3.5" />{v}</span>;

    return (
        <div className="bg-cream min-h-screen pb-14 md:pb-0" data-testid="compare-page">
            <Header />
            <section className="bg-navy text-white py-14">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="font-script text-3xl text-gold -rotate-1 inline-block">saamne rakh ke dekho</p>
                    <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tight" data-testid="compare-h1">Compare Packages.</h1>
                    <p className="mt-3 text-white/70 max-w-2xl">Side by side, line by line. Highlighted cells differ between your picks.</p>
                </div>
            </section>

            <section className="py-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to="/packages" data-testid="compare-back" className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-navy hover:text-saffron"><ArrowLeft className="h-4 w-4" /> Back to packages (selection saved)</Link>

                    {packages === null ? (
                        <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-saffron" /></div>
                    ) : packages.length === 0 ? (
                        <div className="py-20 text-center" data-testid="compare-empty">
                            <p className="font-display font-black text-2xl text-navy">Nothing to compare yet.</p>
                            <p className="mt-2 text-sm text-stone-500">Tick "Compare" on any two or three packages and they'll wait for you here.</p>
                            <Link to="/packages" data-testid="compare-empty-browse" className="mt-6 inline-block rounded-full bg-saffron hover:bg-saffron-dark text-white px-7 py-3.5 text-sm font-display font-black uppercase tracking-wider">Browse Packages</Link>
                        </div>
                    ) : (
                        <>
                            {durationDiffers && (
                                <div className="mt-5 rounded-xl bg-gold/15 border border-gold/40 px-4 py-3 text-sm text-stone-700" data-testid="duration-note">
                                    These packages have different durations ({daysList.join(" vs ")} days) — per-person prices aren't directly equivalent. Compare cost per day and what's included.
                                </div>
                            )}
                            <div className="mt-5 overflow-x-auto rounded-2xl border border-stone-300 bg-white" data-testid="compare-table-wrap">
                                <table className="w-full min-w-[720px]" data-testid="compare-table">
                                    <thead>
                                        <tr className="border-b border-stone-200">
                                            <th className={labelCell}> </th>
                                            {packages.map((p) => (
                                                <th key={p.slug} className="px-4 py-4 align-top text-left min-w-[220px]">
                                                    <div className="relative">
                                                        <button onClick={() => remove(p.slug)} data-testid={`compare-col-remove-${p.slug}`} aria-label={`Remove ${p.name}`} className="absolute -top-1 right-0 text-stone-300 hover:text-red-500"><X className="h-4 w-4" /></button>
                                                        <img src={p.cover_image} alt={p.name} loading="lazy" className="h-20 w-full object-cover rounded-lg" />
                                                        <Link to={`/package/${p.slug}`} className="mt-2 block font-display font-black text-navy hover:text-saffron leading-tight" data-testid={`compare-col-name-${p.slug}`}>{p.name}</Link>
                                                        {(p.tiers || []).length > 1 && (
                                                            <select value={tiers[p.slug]} onChange={(e) => setTiers((t) => ({ ...t, [p.slug]: e.target.value }))} data-testid={`compare-tier-${p.slug}`} className="mt-2 w-full rounded-lg border border-stone-300 px-2 py-1.5 text-xs outline-none focus:border-saffron">
                                                                {p.tiers.map((t) => <option key={t.label} value={t.label}>{t.label}</option>)}
                                                            </select>
                                                        )}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.key} className="border-b border-stone-100" data-testid={`compare-row-${r.key}`}>
                                                <td className={labelCell}>
                                                    {r.label}
                                                    {diffKeys.has(r.key) && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-saffron align-middle" title="Differs" />}
                                                </td>
                                                {packages.map((p) => {
                                                    const v = r.get(p);
                                                    return (
                                                        <td key={p.slug} className={`${cell} ${diffKeys.has(r.key) ? "bg-gold/10" : ""} ${r.money ? "font-display font-black text-navy" : ""}`}>
                                                            {r.key === "insurance" ? insuranceIcon(v) : v}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className={labelCell}> </td>
                                            {packages.map((p) => (
                                                <td key={p.slug} className="px-4 py-4">
                                                    <Link to={`/package/${p.slug}?enquire=1`} data-testid={`compare-enquire-${p.slug}`} className="block text-center rounded-full bg-saffron hover:bg-saffron-dark text-white px-5 py-2.5 text-[11px] font-display font-black uppercase tracking-wider transition-colors">Enquire</Link>
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="mt-3 text-[11px] text-stone-400">Included = in package price · Optional = chargeable add-on · Not included = arrange yourself. Estimates use your saved traveller count with self-arranged transport.</p>
                        </>
                    )}
                </div>
            </section>
            <Footer />
            <WhatsAppFloat /><MobileStickyCTA />
        </div>
    );
}
